import { Box } from "@analyticsMD/quil";
import { Dispatch, FC, SetStateAction, useState, useEffect } from "react";
import MedicallyReadyStatus from "../types/MedicallyReadyStatus";
import { useMutation, useQueryClient } from "react-query";
import {
  patchMedicallyReadyStatus,
  postMedicallyReadyStatus,
} from "../api/endpoints";
import { MedicallyReadyStatusChoices } from "../types/MedicallyReadyStatusChoices";
import { Sources } from "../types/Sources";
import { FormControlLabel, FormGroup, Switch } from "@mui/material";
import { useUpdatePatientCache } from "../hooks/use-update-patient-cache";
import { UI_ACCENT_BLUE, UI_ACCENT_BLUE_HOVER } from "../constants/ui-colors";

interface MedicallyReadyToggleProps {
  medicallyReadyStatus: MedicallyReadyStatus;
  patientID: number;
  disabled?: boolean;
  setShowErrorToast: Dispatch<SetStateAction<boolean>>;
  useSidebarView?: boolean;
}

const MedicallyReadyToggle: FC<MedicallyReadyToggleProps> = ({
  medicallyReadyStatus,
  patientID,
  disabled,
  setShowErrorToast,
  useSidebarView = false,
}) => {
  const isMedicallyReady =
    medicallyReadyStatus?.status === MedicallyReadyStatusChoices.YES;

  const [medicallyReady, setMedicallyReady] = useState(isMedicallyReady);

  useEffect(() => {
    setMedicallyReady(
      medicallyReadyStatus?.status === MedicallyReadyStatusChoices.YES
    );
  }, [medicallyReadyStatus]);

  const queryClient = useQueryClient();
  const { updatePatientMedicallyReady } = useUpdatePatientCache();

  const patchMutation = useMutation(patchMedicallyReadyStatus, {
    onSuccess: (data) => {
      if (!useSidebarView) {
        updatePatientMedicallyReady(patientID, () => data);
      }
      queryClient.invalidateQueries({
        queryKey: ["medicallyready", patientID],
      });
    },
    onError: () => {
      setMedicallyReady(isMedicallyReady);
      setShowErrorToast(true);
    },
  });

  const postMutation = useMutation(postMedicallyReadyStatus, {
    onSuccess: (data) => {
      if (!useSidebarView) {
        updatePatientMedicallyReady(patientID, () => data);
      }
      queryClient.invalidateQueries({
        queryKey: ["medicallyready", patientID],
      });
    },
    onError: () => {
      setMedicallyReady(false);
      setShowErrorToast(true);
    },
  });

  const handleToggleMedicallyReady = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const isMedicallyReady = event.target.checked;
    const status = isMedicallyReady
      ? MedicallyReadyStatusChoices.YES
      : MedicallyReadyStatusChoices.NO;

    setMedicallyReady(isMedicallyReady);

    if (medicallyReadyStatus) {
      patchMutation.mutate({
        patient_id: patientID,
        status: status,
        source: Sources.USER_CREATED,
      });
    } else {
      postMutation.mutate({
        patient: patientID,
        status: status,
        source: Sources.USER_CREATED,
      });
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <FormGroup title="">
        <FormControlLabel
          control={
            <Switch
              checked={medicallyReady}
              onChange={handleToggleMedicallyReady}
              disabled={disabled}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: UI_ACCENT_BLUE,
                  "&:hover": {
                    backgroundColor: UI_ACCENT_BLUE_HOVER,
                  },
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: `${UI_ACCENT_BLUE} !important`,
                  opacity: 1,
                },
                "& .MuiSwitch-switchBase.Mui-disabled": {
                  color: "#BDBDBD",
                },
                "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled": {
                  color: "#BDBDBD",
                },
                "& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track": {
                  backgroundColor: "#BDBDBD !important",
                },
                "& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track":
                  {
                    backgroundColor: "#BDBDBD !important",
                  },
              }}
            />
          }
          label={medicallyReady ? "Yes" : "No"}
          slotProps={{ typography: { sx: { fontSize: "0.875rem" } } }}
        />
      </FormGroup>
    </Box>
  );
};

export default MedicallyReadyToggle;
